/**
 * App-wide upload manager.
 *
 * Founder, 2026-10-05: "If I upload large files and reload the page or move to
 * other pages, I don't see the upload status — I think it stops and I need to
 * re-upload."
 *
 * The queue used to live inside the Library component. Moving to another page
 * unmounted the only UI that knew about the uploads (they kept running,
 * invisibly), and coming back mounted a fresh, empty queue whose resume banner
 * then offered to "resume" uploads that were still going. So the queue, the
 * File refs that make retry possible, the AbortControllers and the worker pool
 * live here, in a module singleton that outlives every route; any surface
 * reads it through useSyncExternalStore.
 *
 * A reload cannot be survived — the page's File objects die with it. What can
 * be done: large files go multipart from the first byte, their resume record
 * is written before the first part, and the global panel offers to continue
 * them (Chromium: one click via a stored file handle; elsewhere: pick the file
 * again).
 */
import {
  putWithProgress, putViaProxy, putMultipart, fileIsReadable, isTransientUploadError,
  uploadId, type UploadItem,
} from './upload';
import {
  resumeStore, findResumeRecord, reconcileResumeRecords, ownedBy, RESUME_WINDOW_MS,
  type ResumeRecord, type ResumeMeta,
} from './uploadResume';
import { setReloadGuard } from './chunkReload';

export type CompleteExtra = { editedFromAssetId?: string; width?: number; height?: number };

export interface UploadJob {
  file: File;
  /** Full target folder, subfolder of a dropped directory included. */
  folder: string;
  overwriteAssetId?: string;
  deferIndexing?: boolean;
  /** Extra upload-complete fields — the editors send editedFromAssetId + output size. */
  completeExtra?: CompleteExtra;
  /** Continue this saved multipart rather than looking one up by file identity. */
  resumeKey?: string;
}

/** Failures the UI words itself (see uploadErrorText). Server codes pass through too. */
export type UploadErrorCode = 'EMPTY_FILE' | 'FILE_GONE' | 'UPLOADS_SUSPENDED' | string;

export type UploadResult =
  | { ok: true }
  | { ok: false; error: string; code?: UploadErrorCode; canceled?: boolean };

export interface UploadDeps {
  apiBase: string;
  token: () => string;
}

export interface RunContext {
  /** Is this r2Key being uploaded right now by this tab? */
  busy: (r2Key: string) => boolean;
  /** Keys other tabs hold (Web Locks); absent where the API is missing. */
  heldElsewhere?: () => Promise<Set<string>>;
  /** Mark an r2Key as ours for the rest of this upload. */
  claim: (r2Key: string) => void;
  owner: string | null;
  handle?: FileSystemFileHandle;
}

export type Runner = (
  job: UploadJob,
  onProgress: (loaded: number, total: number) => void,
  signal: AbortSignal,
  ctx: RunContext,
) => Promise<UploadResult>;

/** Above this a file goes multipart from the start, so a reload leaves something to resume. */
export const MULTIPART_MIN = 100 * 1024 * 1024;
/** A single R2 PUT cannot exceed 5 GiB (EntityTooLarge). */
const SINGLE_PUT_MAX = 5 * 1024 * 1024 * 1024;
const PROXY_CAP = 80 * 1024 * 1024;

/** May a plain re-add continue this record? Only into the folder its key was minted for. */
export function implicitResumeFits(rec: Pick<ResumeRecord, 'folder'>, folder: string): boolean {
  return rec.folder !== undefined && rec.folder === folder;
}

export function ownerFromToken(tok: string): string | null {
  try {
    const p = JSON.parse(atob((tok.split('.')[1] || '').replace(/-/g, '+').replace(/_/g, '/')));
    return typeof p?.sub === 'string' ? p.sub : null;
  } catch { return null; }
}

/**
 * One file, every transport. Moved whole from StudioLibrary's uploadOne; it
 * returns codes instead of setting page state, because the page that started
 * it may be long gone by the time it fails.
 */
export async function uploadFile(
  deps: UploadDeps,
  job: UploadJob,
  onProgress: (loaded: number, total: number) => void,
  signal: AbortSignal,
  ctx: RunContext,
): Promise<UploadResult> {
  const { file, folder: targetFolder, overwriteAssetId, deferIndexing, completeExtra } = job;
  const API = deps.apiBase;
  const tok = deps.token();
  const startedAt = Date.now();
  // Which transport was live when it died — a presigned-PUT failure and a
  // multipart failure have completely different causes, and the server
  // cannot tell them apart on its own (the bytes never reach it).
  let transport: 'put' | 'proxy' | 'multipart' | 'resume' = 'put';

  // Report a failure the SERVER cannot see. Browser→R2 uploads never touch
  // the Worker, so before this the only record of a failed upload was a
  // console.warn in someone's browser — which is how a 93-file batch lost 5
  // files while prod logs showed 100% success. Fire-and-forget by design:
  // it must never delay, block, or itself fail an upload.
  const beacon = (stage: string, error: string) => {
    try {
      void fetch(`${API}/media-library/upload-failed`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${tok}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          filename: file.name, size: file.size, stage, transport, error,
          durationMs: Date.now() - startedAt,
        }),
        keepalive: true, // still sent if the user closes the tab on a failure
      }).catch(() => {});
    } catch { /* telemetry is never a failure path */ }
  };
  // A dropped FOLDER, or a file the OS won't let the page read, surfaces as
  // a 0-byte File. It produced a real, confusing failure downstream (the
  // multipart path made zero chunks and upload-complete 400'd), so reject
  // it here with a message that names the actual problem.
  if (!file.size) {
    const msg = `"${file.name}" is empty or unreadable (folders drop as 0-byte entries — drop the files inside, not the folder).`;
    beacon('unreadable', msg);
    return { ok: false, error: msg, code: 'EMPTY_FILE' };
  }
  const meta = (): ResumeMeta => ({
    folder: targetFolder,
    ...(overwriteAssetId ? { overwriteAssetId } : {}),
    ...(deferIndexing ? { deferIndexing: true } : {}),
    ...(completeExtra ? { completeExtra } : {}),
    ...(ctx.owner ? { owner: ctx.owner } : {}),
    ...(ctx.handle ? { handle: ctx.handle } : {}),
    createdAt: Date.now(),
  });
  // RESUME: if this exact file (name+size+lastModified) has a saved
  // interrupted multipart upload, continue it — skip already-uploaded
  // chunks instead of restarting. Keyed by file identity because a fresh
  // upload-init would mint a NEW r2 key and lose the in-progress parts.
  let saved: ResumeRecord | null = null;
  try {
    // Another tab may be sending this very file; continuing it from here too
    // would upload every part twice.
    const elsewhere = ctx.heldElsewhere ? await ctx.heldElsewhere().catch(() => new Set<string>()) : new Set<string>();
    const busy = (k: string) => ctx.busy(k) || elsewhere.has(k);
    if (job.resumeKey) {
      // Explicit Resume (panel button / re-picked file): the job was built from
      // the record, so it already carries the record's folder and choices.
      const rec = await resumeStore.get(job.resumeKey);
      saved = rec && !rec.queued && rec.uploadId && ownedBy(rec, ctx.owner)
        && resumeStore.matches(rec, file) && !busy(rec.r2Key) ? rec : null;
    }
    if (!saved) {
      // A plain re-add. Its own folder and conflict answer win; the saved parts
      // are reused only when they were bound for the same folder, because the
      // folder is part of the R2 key. Otherwise the stale record goes and the
      // file starts fresh where the user put it.
      const found = findResumeRecord(await resumeStore.all(), file, { owner: ctx.owner, busy });
      if (found && implicitResumeFits(found, targetFolder)) saved = found;
      else if (found) await resumeStore.delete(found.r2Key).catch(() => {});
    }
    if (saved) {
      transport = 'resume';
      ctx.claim(saved.r2Key);
      await putMultipart(API, tok, saved.r2Key, file, {
        contentType: saved.contentType, signal, resume: saved,
        // Never re-own a record: it stays with the account that started it.
        meta: { ...meta(), owner: saved.owner, createdAt: saved.createdAt ?? saved.updatedAt, ...(saved.handle ? { handle: saved.handle } : {}) },
        onProgress: (loaded, total) => onProgress(loaded, total),
      });
      const completeRes = await fetch(`${API}/media-library/upload-complete`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${tok}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          r2Key: saved.r2Key, originalFilename: file.name, mime: saved.contentType, folder: targetFolder,
          ...(overwriteAssetId ? { overwriteAssetId } : {}),
          ...(deferIndexing ? { deferIndexing: true } : {}),
          ...(completeExtra || {}),
        }),
      });
      const cj: any = await completeRes.json();
      if (!completeRes.ok || !cj?.success) throw new Error(cj?.error || `Complete ${completeRes.status}`);
      return { ok: true };
    }
  } catch (e: any) {
    if (e?.name === 'AbortError') return { ok: false, error: 'Canceled', canceled: true };
    // Resume failed (server aborted the multipart, or record stale) — drop
    // the record and fall through to a fresh upload.
    console.warn('[upload] resume failed, starting fresh:', e?.message);
    // Back to the default path — otherwise a later failure is reported as
    // transport 'resume', and telling a presigned-PUT failure apart from a
    // multipart one is the entire point of the field.
    transport = 'put';
    // A key refusal means the record is not this account's to touch.
    const refused = /invalid upload key|does not belong/i.test(String(e?.message || ''));
    if (saved && !refused) await resumeStore.delete(saved.r2Key).catch(() => {});
  }
  try {
    const initRes = await fetch(`${API}/media-library/upload-init`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${tok}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ filename: file.name, mime: file.type, folder: targetFolder, size: file.size }),
    });
    const initJ: any = await initRes.json();
    if (!initRes.ok || !initJ?.success) {
      beacon('init', initJ?.error || `Init ${initRes.status}`);
      // Plan / size / suspension errors carry a code so whichever surface is
      // showing the queue can word them; the server message is the fallback.
      const err = new Error(initJ?.error || `Init ${initRes.status}`);
      (err as any).code = initJ?.code;
      throw err;
    }
    ctx.claim(initJ.r2Key);
    const onP = (loaded: number, total: number) => onProgress(loaded, total);
    if (file.size > MULTIPART_MIN) {
      // Big files go chunked from the start, not only past the 5 GiB single-PUT
      // ceiling: a single PUT leaves nothing behind when the page reloads, so
      // a 4 GB upload at 90% restarted from zero.
      transport = 'multipart';
      try {
        await putMultipart(API, tok, initJ.r2Key, file, { contentType: initJ.mime, signal, meta: meta(), onProgress: onP });
      } catch (mpuErr: any) {
        // Only a failure to START multipart may fall back: nothing was sent and
        // the presigned URL from init is seconds old. Past 5 GiB there is no
        // single-PUT path to fall back to.
        if (mpuErr?.name === 'AbortError' || mpuErr?.stage !== 'create' || file.size > SINGLE_PUT_MAX) throw mpuErr;
        console.warn('[upload] multipart unavailable, using a single PUT:', mpuErr?.message);
        beacon('mpu-escalated', mpuErr?.message || 'multipart create failed');
        transport = 'put';
        await putWithProgress(initJ.uploadUrl, file, { contentType: initJ.mime, signal, expiresInSec: initJ.expiresIn, onProgress: onP });
      }
    } else {
      try {
        await putWithProgress(initJ.uploadUrl, file, { contentType: initJ.mime, signal, expiresInSec: initJ.expiresIn, onProgress: onP });
      } catch (putErr: any) {
        // Escalation chain, driven by what prod actually showed:
        //  1. presigned PUT — blocked in some environments (R2 domain refused)
        //  2. same-origin PUT proxy — some security stacks block ALL raw-body
        //     PUTs regardless of domain, and it is Worker-body-capped anyway
        //  3. chunked multipart POSTs — the request shape everything allows,
        //     no size ceiling. Slowest, so it is last, not first.
        //
        // Only a user cancel stops here. This used to demand the word
        // "network" in the message, so a stall, a timeout or an expired
        // signature — the three things a multi-GB upload actually hits — were
        // all treated as fatal and never reached step 3. Multipart is the
        // documented "every environment, every size" path: the cost of trying
        // it unnecessarily is a slower upload, the cost of skipping it is a
        // dead file.
        if (putErr?.name === 'AbortError') throw putErr;
        console.warn('[upload] direct PUT failed, escalating:', putErr?.message);
        // Record the escalation even when the fallback goes on to succeed —
        // a file that only uploads via multipart is a signal worth having
        // BEFORE it becomes a file that does not upload at all.
        beacon('put-escalated', putErr?.message || 'direct PUT failed');
        try {
          if (file.size > PROXY_CAP) throw new Error('file exceeds proxy cap — go straight to multipart', { cause: putErr });
          console.warn('[upload] direct R2 PUT blocked, trying same-origin proxy');
          transport = 'proxy';
          await putViaProxy(API, tok, initJ.r2Key, file, { contentType: initJ.mime, signal, onProgress: onP });
        } catch (proxyErr: any) {
          if (proxyErr?.name === 'AbortError') throw proxyErr;
          console.warn('[upload] proxy unavailable, using chunked multipart:', proxyErr?.message);
          transport = 'multipart';
          await putMultipart(API, tok, initJ.r2Key, file, { contentType: initJ.mime, signal, meta: meta(), onProgress: onP });
        }
      }
    }
    const completeRes = await fetch(`${API}/media-library/upload-complete`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${tok}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({
        r2Key: initJ.r2Key,
        originalFilename: file.name,
        mime: initJ.mime || file.type,
        folder: targetFolder,
        ...(overwriteAssetId ? { overwriteAssetId } : {}),
        // Bulk uploads defer AI indexing to the throttled cron so a big
        // batch doesn't fire hundreds of concurrent Fly calls at once.
        ...(deferIndexing ? { deferIndexing: true } : {}),
        ...(completeExtra || {}),
      }),
    });
    const completeJ: any = await completeRes.json();
    if (!completeRes.ok || !completeJ?.success) throw new Error(completeJ?.error || `Complete ${completeRes.status}`);
    return { ok: true };
  } catch (e: any) {
    if (e?.name === 'AbortError') return { ok: false, error: 'Canceled', canceled: true };
    const msg = e?.message || 'Upload failed';
    console.warn('[upload] failed for', file.name, msg);
    // The terminal failure — every fallback in the chain is exhausted. This
    // is the line that makes a lost file visible server-side.
    // "failed (network)" is also what XHR says when the FILE could not be
    // read (changed/moved on disk, drive or sync folder gone). Record which
    // one it was so the next report is not a guess (2026-09-25: five files
    // failed together and the evidence could not separate the two).
    const sourceReadable = isTransientUploadError(e) ? await fileIsReadable(file) : true;
    beacon('failed', sourceReadable ? msg : `${msg} [source file unreadable]`);
    return { ok: false, error: msg, ...(e?.code ? { code: e.code } : {}) };
  }
}

export interface InterruptedUpload {
  key: string;
  fileName: string;
  fileSize: number;
  folder?: string;
  /** ms epoch the server-side multipart opened; the 48 h window counts from here. */
  startedAt?: number;
  /** Chromium: can be resumed with one click, no file picker. */
  hasHandle: boolean;
}

export interface BatchSummary {
  ok: number;
  failed: number;
  total: number;
  /** Failure codes in the batch, for surfaces that word plan/size limits. */
  codes: string[];
  /** Server messages for the coded failures, same order as codes. */
  messages: string[];
  /** An editor's save: the editor reports its own outcome and refreshes itself. */
  fromEditor: boolean;
}

export interface UploadManagerState {
  items: UploadItem[];
  active: boolean;
  interrupted: InterruptedUpload[];
  /** Surfaces showing their own inline strip; the floating panel steps aside for them. */
  inlineViews: number;
  /** A new deploy wanted to reload the page while uploads ran; it waited. */
  reloadDeferred: boolean;
}

/** Narrow storage surface, so tests can run without IndexedDB. */
export interface ResumeStoreLike {
  put(rec: ResumeRecord): Promise<void>;
  get(key: string): Promise<ResumeRecord | null>;
  all(): Promise<ResumeRecord[]>;
  delete(key: string): Promise<void>;
}

export interface ManagerOptions {
  run: Runner;
  concurrency?: number;
  clearAfterMs?: number;
  store?: ResumeStoreLike;
  /** The server's still-open multipart uploads, or null when unreadable. */
  fetchOpen?: () => Promise<Array<{ r2_key: string; created_at?: string }> | null>;
  owner?: () => string | null;
  signedIn?: () => boolean;
}

type Batch = { ids: Set<string>; ok: number; failed: number; codes: string[]; messages: string[]; total: number; fromEditor: boolean };

const LOCK_PREFIX = 'soltik-upload:';
const TERMINAL = new Set(['done', 'error']);

export class UploadManager {
  private items: UploadItem[] = [];
  private jobs = new Map<string, UploadJob>();
  private pending: string[] = [];
  private running = new Map<string, AbortController>();
  private batches = new Map<string, Batch>();
  private batchOf = new Map<string, string>();
  private waiters = new Map<string, (r: UploadResult) => void>();
  private claimed = new Map<string, Set<string>>(); // item id -> r2Keys
  private keysOf = new Map<string, Set<string>>(); // item id -> r2Keys, until the row is gone
  private editorIds = new Set<string>();
  private releases = new Map<string, () => void>(); // r2Key -> lock release
  private queuedKeys = new Map<string, string>(); // item id -> queued record key
  private queuedWrites = new Map<string, Promise<unknown>>();
  private handles = new WeakMap<File, FileSystemFileHandle>();
  private interruptedRecs: ResumeRecord[] = [];
  private startedAt = new Map<string, number>();
  private inlineViews = 0;
  private reloadDeferred = false;
  private listeners = new Set<() => void>();
  private settledListeners = new Set<(s: BatchSummary) => void>();
  private state: UploadManagerState = { items: [], active: false, interrupted: [], inlineViews: 0, reloadDeferred: false };
  private unloadHooked = false;
  private readonly concurrency: number;
  private readonly clearAfterMs: number;
  private readonly store: ResumeStoreLike;

  constructor(private readonly opts: ManagerOptions) {
    this.concurrency = opts.concurrency ?? 3;
    this.clearAfterMs = opts.clearAfterMs ?? 2500;
    this.store = opts.store ?? resumeStore;
  }

  // ── store plumbing ────────────────────────────────────────────────
  subscribe = (fn: () => void): (() => void) => {
    this.listeners.add(fn);
    return () => { this.listeners.delete(fn); };
  };

  getSnapshot = (): UploadManagerState => this.state;

  /** Fires once per enqueue() call when every file in it has finished or failed. */
  onBatchSettled(fn: (s: BatchSummary) => void): () => void {
    this.settledListeners.add(fn);
    return () => { this.settledListeners.delete(fn); };
  }

  isActive(): boolean {
    return this.state.active;
  }

  private emit() {
    const active = this.items.some((u) => u.status === 'pending' || u.status === 'uploading');
    this.state = {
      items: this.items,
      active,
      interrupted: this.interruptedRecs.map((r) => ({
        key: r.r2Key,
        fileName: r.fileName,
        fileSize: r.fileSize,
        folder: r.folder,
        startedAt: this.startedAt.get(r.r2Key) ?? r.createdAt ?? r.updatedAt,
        hasHandle: !!r.handle,
      })),
      inlineViews: this.inlineViews,
      reloadDeferred: this.reloadDeferred,
    };
    this.syncUnloadGuard(active);
    for (const fn of this.listeners) fn();
  }

  private patch(id: string, p: Partial<UploadItem>) {
    this.items = this.items.map((u) => (u.id === id ? { ...u, ...p } : u));
  }

  // ── queue ─────────────────────────────────────────────────────────
  /**
   * Register files and start them; returns their entries, in the order given.
   * `priority` starts them at once, past the concurrency cap — a user waiting
   * on a modal must not queue behind a 90-file drop.
   */
  enqueue(jobs: UploadJob[], opts: { priority?: boolean; fromEditor?: boolean } = {}): UploadItem[] {
    if (jobs.length === 0) return [];
    const batchId = uploadId();
    const entries: UploadItem[] = jobs.map((j) => ({
      id: uploadId(), name: j.file.name, size: j.file.size, loaded: 0, status: 'pending',
    }));
    this.batches.set(batchId, {
      ids: new Set(entries.map((e) => e.id)), ok: 0, failed: 0, codes: [], messages: [], total: entries.length,
      fromEditor: !!opts.fromEditor,
    });
    entries.forEach((e, i) => {
      this.jobs.set(e.id, jobs[i]);
      this.batchOf.set(e.id, batchId);
      if (opts.fromEditor) this.editorIds.add(e.id);
      if (!opts.priority) this.pending.push(e.id);
    });
    this.items = [...this.items, ...entries];
    this.emit();
    if (opts.priority) for (const e of entries) this.start(e.id);
    this.pump();
    // Whatever is still waiting for a slot gets a record too, so a reload
    // before its turn does not drop it without a trace.
    for (const e of entries) if (this.pending.includes(e.id)) this.persistQueued(e.id);
    return entries;
  }

  /**
   * The image editor's save: starts now, whatever else is queued, and
   * resolves with the outcome. A failure is the editor's to show — its row
   * leaves the strip rather than offering a Retry the editor knows nothing of.
   */
  uploadAndWait(job: UploadJob): Promise<UploadResult> {
    const [entry] = this.enqueue([job], { priority: true, fromEditor: true });
    if (!entry) return Promise.resolve({ ok: false, error: 'Nothing to upload' });
    return new Promise((resolve) => this.waiters.set(entry.id, resolve));
  }

  private pump() {
    while (this.running.size < this.concurrency && this.pending.length > 0) {
      const id = this.pending.shift()!;
      this.start(id);
    }
  }

  private start(id: string) {
    const job = this.jobs.get(id);
    if (!job) return;
    const ctrl = new AbortController();
    this.running.set(id, ctrl);
    this.dropQueued(id);
    this.patch(id, { status: 'uploading' });
    this.emit();
    const ctx: RunContext = {
      busy: (k) => this.isBusy(k),
      heldElsewhere: heldLockKeys,
      claim: (k) => this.claim(id, k),
      owner: this.opts.owner?.() ?? null,
      handle: this.handles.get(job.file),
    };
    const onProgress = (loaded: number) => {
      if (!this.running.has(id)) return;
      this.patch(id, { loaded, status: 'uploading' });
      this.emit();
    };
    let result: Promise<UploadResult>;
    try {
      result = this.opts.run(job, onProgress, ctrl.signal, ctx);
    } catch (e: any) {
      result = Promise.resolve({ ok: false, error: e?.message || 'Upload failed' });
    }
    result
      .catch((e: any): UploadResult => ({ ok: false, error: e?.message || 'Upload failed' }))
      .then((r) => this.finish(id, ctrl.signal.aborted && !r.ok ? { ok: false, error: 'Canceled', canceled: true } : r));
  }

  private finish(id: string, r: UploadResult) {
    this.running.delete(id);
    this.releaseClaims(id);
    const batch = this.batches.get(this.batchOf.get(id) || '');
    const present = this.items.some((u) => u.id === id);
    const fromEditor = this.editorIds.delete(id);
    if (r.ok) {
      this.keysOf.delete(id);
      this.jobs.delete(id);
      if (present) {
        const size = this.items.find((u) => u.id === id)!.size;
        this.patch(id, { status: 'done', loaded: size, error: undefined, code: undefined });
      }
      if (batch) batch.ok++;
    } else if (r.canceled || !present) {
      // cancel() already removed the row. A cancelled upload is not
      // "interrupted": forget its resume record so it never comes back as one.
      this.forgetRecords(id);
      this.jobs.delete(id);
      this.items = this.items.filter((u) => u.id !== id);
      if (batch) batch.total--;
    } else if (fromEditor) {
      this.jobs.delete(id);
      this.items = this.items.filter((u) => u.id !== id);
      if (batch) batch.failed++;
    } else {
      this.patch(id, { status: 'error', error: r.error, code: r.code });
      if (batch) {
        batch.failed++;
        if (r.code) { batch.codes.push(r.code); batch.messages.push(r.error); }
      }
    }
    const waiter = this.waiters.get(id);
    if (waiter) { this.waiters.delete(id); waiter(r); }
    this.emit();
    this.settleIfDone(this.batchOf.get(id));
    this.pump();
  }

  private settleIfDone(batchId?: string) {
    if (!batchId) return;
    const batch = this.batches.get(batchId);
    if (!batch) return;
    const unfinished = [...batch.ids].some((id) => {
      const u = this.items.find((x) => x.id === id);
      return !!u && !TERMINAL.has(u.status);
    });
    if (unfinished) return;
    this.batches.delete(batchId);
    for (const id of batch.ids) this.batchOf.delete(id);
    if (batch.total > 0) {
      const summary: BatchSummary = {
        ok: batch.ok, failed: batch.failed, total: batch.total, codes: batch.codes, messages: batch.messages, fromEditor: batch.fromEditor,
      };
      for (const fn of this.settledListeners) {
        try { fn(summary); } catch { /* a listener never breaks the queue */ }
      }
    }
    // Keep finished rows on screen for a beat, so a quick upload still shows
    // a completed state. Failures are KEPT until dismissed or retried.
    setTimeout(() => {
      const before = this.items.length;
      this.items = this.items.filter((u) => !batch.ids.has(u.id) || u.status !== 'done');
      if (this.items.length !== before) this.emit();
    }, this.clearAfterMs);
  }

  cancel(id: string) {
    const ctrl = this.running.get(id);
    const wasPending = this.pending.includes(id);
    this.pending = this.pending.filter((x) => x !== id);
    this.dropQueued(id);
    this.items = this.items.filter((u) => u.id !== id);
    if (ctrl) {
      ctrl.abort(); // finish() runs when the transport rejects
      this.emit();
      return;
    }
    if (wasPending) { this.finish(id, { ok: false, error: 'Canceled', canceled: true }); return; }
    // An errored row: nothing in flight, just drop it.
    this.jobs.delete(id);
    this.keysOf.delete(id);
    this.emit();
  }

  cancelAll() {
    const ids = this.items.filter((u) => u.status === 'pending' || u.status === 'uploading').map((u) => u.id);
    for (const id of ids) this.cancel(id);
  }

  /**
   * Drop errored rows. Pass ids to drop ONLY those — the unscoped form would
   * also clear a failure that landed after the caller last rendered, which
   * the user would then never see.
   */
  clearErrors(ids?: string[]) {
    const only = ids ? new Set(ids) : null;
    const dropped = this.items.filter((u) => u.status === 'error' && (!only || only.has(u.id)));
    if (dropped.length === 0) return;
    // Dismissed means given up on: its saved parts must not resurface later as
    // an "interrupted" upload, or be picked up by an unrelated re-add.
    for (const u of dropped) { this.jobs.delete(u.id); this.forgetRecords(u.id); }
    const gone = new Set(dropped.map((u) => u.id));
    this.items = this.items.filter((u) => !gone.has(u.id));
    this.emit();
  }

  /** Retry only the FAILED files, with the File objects kept from the first try. */
  retryFailed() {
    const failed = this.items.filter((u) => u.status === 'error' && this.jobs.has(u.id));
    if (failed.length === 0) return;
    const jobs = failed.map((u) => this.jobs.get(u.id)!);
    const gone = new Set(failed.map((u) => u.id));
    for (const id of gone) { this.jobs.delete(id); this.keysOf.delete(id); }
    this.items = this.items.filter((u) => !gone.has(u.id));
    this.enqueue(jobs);
  }

  /** Retry ONE failed file. */
  retryOne(id: string) {
    const job = this.jobs.get(id);
    if (!job) {
      // The control is visible, so it must do something.
      this.patch(id, { status: 'error', error: 'File no longer available — re-add it to retry', code: 'FILE_GONE' });
      this.emit();
      return;
    }
    this.jobs.delete(id);
    this.keysOf.delete(id);
    this.items = this.items.filter((u) => u.id !== id);
    this.enqueue([job]);
  }

  // ── r2Key ownership ───────────────────────────────────────────────
  private claim(id: string, key: string) {
    const set = this.claimed.get(id) || new Set<string>();
    set.add(key);
    this.claimed.set(id, set);
    const kept = this.keysOf.get(id) || new Set<string>();
    kept.add(key);
    this.keysOf.set(id, kept);
    if (!this.releases.has(key)) this.releases.set(key, holdLock(key));
    const before = this.interruptedRecs.length;
    this.interruptedRecs = this.interruptedRecs.filter((r) => r.r2Key !== key);
    if (this.interruptedRecs.length !== before) this.emit();
  }

  private forgetRecords(id: string) {
    const keys = this.keysOf.get(id);
    this.keysOf.delete(id);
    for (const key of keys || []) {
      void this.store.delete(key).catch(() => {});
      // A part write already on its way to IndexedDB when the cancel landed
      // could put the record back; delete once more after it has settled.
      setTimeout(() => { void this.store.delete(key).catch(() => {}); }, 2000);
    }
  }

  private releaseClaims(id: string) {
    for (const key of this.claimed.get(id) || []) {
      this.releases.get(key)?.();
      this.releases.delete(key);
    }
    this.claimed.delete(id);
  }

  private isBusy(key: string, heldElsewhere?: Set<string>): boolean {
    if (this.releases.has(key)) return true;
    for (const k of this.queuedKeys.values()) if (k === key) return true;
    return !!heldElsewhere?.has(key);
  }

  // ── queued (not yet started) records ──────────────────────────────
  rememberHandle(file: File, handle: FileSystemFileHandle) {
    this.handles.set(file, handle);
  }

  private persistQueued(id: string) {
    const job = this.jobs.get(id);
    if (!job || job.resumeKey || job.file.size <= MULTIPART_MIN) return;
    const key = `queued:${id}`;
    this.queuedKeys.set(id, key);
    const handle = this.handles.get(job.file);
    const owner = this.opts.owner?.() ?? undefined;
    const write = this.store.put({
      r2Key: key, uploadId: '', queued: true,
      fileName: job.file.name, fileSize: job.file.size, fileLastModified: job.file.lastModified,
      contentType: job.file.type || 'application/octet-stream', chunkSize: 0, parts: [],
      folder: job.folder,
      ...(job.overwriteAssetId ? { overwriteAssetId: job.overwriteAssetId } : {}),
      ...(job.deferIndexing ? { deferIndexing: true } : {}),
      ...(job.completeExtra ? { completeExtra: job.completeExtra } : {}),
      ...(owner ? { owner } : {}),
      ...(handle ? { handle } : {}),
      createdAt: Date.now(), updatedAt: Date.now(),
    }).catch(() => {});
    this.queuedWrites.set(key, write);
    this.releases.set(key, holdLock(key));
  }

  private dropQueued(id: string) {
    const key = this.queuedKeys.get(id);
    if (!key) return;
    this.queuedKeys.delete(id);
    this.releases.get(key)?.();
    this.releases.delete(key);
    // After the write, never before it — a delete that wins the race leaves
    // the record behind as a phantom "interrupted" upload.
    const write = this.queuedWrites.get(key) || Promise.resolve();
    this.queuedWrites.delete(key);
    void write.then(() => this.store.delete(key)).catch(() => {});
  }

  // ── interrupted uploads (after a reload) ──────────────────────────
  /**
   * Reconcile saved records against the server's open multipart uploads and
   * against what any tab is uploading right now. Records for keys in flight
   * are skipped — offering to "resume" a live upload is the false banner the
   * Library used to show after navigating back to it.
   */
  async refreshInterrupted(): Promise<void> {
    if (this.opts.signedIn && !this.opts.signedIn()) {
      if (this.interruptedRecs.length) { this.interruptedRecs = []; this.emit(); }
      return;
    }
    try {
      const records = await this.store.all();
      if (records.length === 0) {
        if (this.interruptedRecs.length) { this.interruptedRecs = []; this.emit(); }
        return;
      }
      const needsServer = records.some((r) => !r.queued);
      const open = needsServer && this.opts.fetchOpen ? await this.opts.fetchOpen().catch(() => null) : null;
      const openKeys = open ? new Set(open.map((u) => u.r2_key)) : null;
      for (const u of open || []) {
        const t = u.created_at ? Date.parse(u.created_at) : NaN;
        if (Number.isFinite(t)) this.startedAt.set(u.r2_key, t);
      }
      // Who is uploading what is read AFTER the fetch: an upload that started
      // while it was in flight must not be listed (or deleted) as interrupted.
      const heldElsewhere = await heldLockKeys();
      const busy = (k: string) => this.isBusy(k, heldElsewhere);
      const { live, stale } = reconcileResumeRecords(records, openKeys, { owner: this.opts.owner?.() ?? null, busy });
      for (const r of stale) if (!this.isBusy(r.r2Key)) await this.store.delete(r.r2Key).catch(() => {});
      this.interruptedRecs = live.filter((r) => !this.isBusy(r.r2Key));
      this.emit();
    } catch { /* resume UI is best-effort */ }
  }

  /**
   * Ask to read every stored handle again, all at once. Must be called
   * synchronously inside the click — requestPermission needs the gesture,
   * and an await before the later calls would spend it on the first one.
   */
  requestResumePermissions(keys: string[]): Promise<Map<string, string>> {
    const asks = keys.map((key) => {
      const h: any = this.interruptedRecs.find((r) => r.r2Key === key)?.handle;
      let p: Promise<string>;
      try {
        p = !h ? Promise.resolve('no-handle')
          : typeof h.requestPermission === 'function' ? Promise.resolve(h.requestPermission({ mode: 'read' })) : Promise.resolve('granted');
      } catch { p = Promise.resolve('denied'); }
      return p.catch(() => 'denied').then((state) => [key, String(state)] as const);
    });
    return Promise.all(asks).then((pairs) => new Map(pairs));
  }

  /**
   * Chromium: continue from the stored file handle. Call straight from a
   * click — requestPermission needs the user gesture — or pass the answer
   * already obtained by requestResumePermissions.
   */
  async resumeInterrupted(key: string, permission?: string): Promise<'resumed' | 'denied' | 'missing' | 'changed' | 'no-handle'> {
    const rec = this.interruptedRecs.find((r) => r.r2Key === key);
    if (!rec?.handle) return 'no-handle';
    const h: any = rec.handle;
    try {
      const perm = permission
        ?? (typeof h.requestPermission === 'function' ? await h.requestPermission({ mode: 'read' }) : 'granted');
      if (perm !== 'granted') return 'denied';
    } catch { return 'denied'; }
    let file: File;
    try { file = await h.getFile(); } catch { return 'missing'; }
    if (!resumeStore.matches(rec, file)) return 'changed';
    this.handles.set(file, rec.handle);
    this.enqueueResume(rec, file);
    return 'resumed';
  }

  /** Other browsers: the user picks the files again; match them to records. */
  resumeWithFiles(files: File[]): { resumed: number; unmatched: string[] } {
    let resumed = 0;
    const unmatched: string[] = [];
    for (const f of files) {
      const rec = this.interruptedRecs.find((r) => resumeStore.matches(r, f));
      if (rec) { this.enqueueResume(rec, f); resumed++; } else unmatched.push(f.name);
    }
    return { resumed, unmatched };
  }

  private enqueueResume(rec: ResumeRecord, file: File) {
    this.interruptedRecs = this.interruptedRecs.filter((r) => r.r2Key !== rec.r2Key);
    if (rec.queued) void this.store.delete(rec.r2Key).catch(() => {});
    this.enqueue([{
      file,
      folder: rec.folder ?? 'library',
      ...(rec.overwriteAssetId ? { overwriteAssetId: rec.overwriteAssetId } : {}),
      ...(rec.deferIndexing ? { deferIndexing: true } : {}),
      ...(rec.completeExtra ? { completeExtra: rec.completeExtra } : {}),
      ...(rec.queued ? {} : { resumeKey: rec.r2Key }),
    }]);
  }

  /** Forget interrupted uploads (all, or one). The server sweeps the parts after 48 h. */
  async discardInterrupted(key?: string): Promise<void> {
    const drop = key ? this.interruptedRecs.filter((r) => r.r2Key === key) : this.interruptedRecs;
    const keys = new Set(drop.map((r) => r.r2Key));
    this.interruptedRecs = this.interruptedRecs.filter((r) => !keys.has(r.r2Key));
    this.emit();
    for (const k of keys) await this.store.delete(k).catch(() => {});
  }

  // ── page-level concerns ───────────────────────────────────────────
  registerInlineView(): () => void {
    this.inlineViews++;
    this.emit();
    let done = false;
    return () => {
      if (done) return;
      done = true;
      this.inlineViews--;
      this.emit();
    };
  }

  noteReloadDeferred() {
    if (this.reloadDeferred) return;
    this.reloadDeferred = true;
    this.emit();
  }

  private onBeforeUnload = (e: BeforeUnloadEvent) => {
    e.preventDefault();
    // Legacy browsers only show the prompt when returnValue is set.
    e.returnValue = '';
  };

  private syncUnloadGuard(active: boolean) {
    if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') return;
    if (active && !this.unloadHooked) {
      window.addEventListener('beforeunload', this.onBeforeUnload);
      this.unloadHooked = true;
    } else if (!active && this.unloadHooked) {
      window.removeEventListener('beforeunload', this.onBeforeUnload);
      this.unloadHooked = false;
    }
  }
}

/** Web Locks let a second tab see which uploads the first one is still running. */
function holdLock(key: string): () => void {
  const locks = typeof navigator !== 'undefined' ? (navigator as any).locks : undefined;
  if (!locks?.request) return () => {};
  let release: () => void = () => {};
  const held = new Promise<void>((r) => { release = r; });
  locks.request(LOCK_PREFIX + key, () => held).catch(() => {});
  return release;
}

async function heldLockKeys(): Promise<Set<string>> {
  const locks = typeof navigator !== 'undefined' ? (navigator as any).locks : undefined;
  if (!locks?.query) return new Set();
  try {
    const snap = await locks.query();
    return new Set<string>((snap?.held || [])
      .map((l: any) => String(l?.name || ''))
      .filter((n: string) => n.startsWith(LOCK_PREFIX))
      .map((n: string) => n.slice(LOCK_PREFIX.length)));
  } catch { return new Set(); }
}

/** Is the stored resume window over for an interrupted upload? */
export function resumeDeadline(startedAt?: number): number | null {
  return startedAt ? startedAt + RESUME_WINDOW_MS : null;
}

// ── the app's singleton ────────────────────────────────────────────
const apiBase = () => ((typeof window !== 'undefined' && window.location?.origin) ? `${window.location.origin}/api` : '/api');
export function authToken(): string {
  try {
    return localStorage.getItem('soltik_auth_token') || localStorage.getItem('authToken') || '';
  } catch { return ''; }
}

export const uploadManager = new UploadManager({
  run: (job, onProgress, signal, ctx) => uploadFile({ apiBase: apiBase(), token: authToken }, job, onProgress, signal, ctx),
  owner: () => ownerFromToken(authToken()),
  signedIn: () => !!authToken(),
  fetchOpen: async () => {
    const res = await fetch(`${apiBase()}/media-library/upload-mpu-open`, { headers: { Authorization: `Bearer ${authToken()}` } });
    if (!res.ok) return null;
    const j: any = await res.json().catch(() => null);
    return Array.isArray(j?.uploads) ? j.uploads : null;
  },
});

// A new deploy's chunk failure must not reload the page under a running
// upload — the bytes in flight would be lost. The panel says why it waited.
setReloadGuard(() => {
  if (!uploadManager.isActive()) return false;
  uploadManager.noteReloadDeferred();
  return true;
});
